import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { hackApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Zap, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Hack, HackStatus } from '@/types/learningManagement';

export const HackListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterComplexity, setFilterComplexity] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'hack');
  const canEdit = hasPermission('edit', 'hack');
  const canDelete = hasPermission('delete', 'hack');

  // Build fetch function with conditional API calls
  const fetchHacksFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return hackApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else if (filterCategory !== 'all') {
        return hackApi.listByCategory(filterCategory, { page: params.page, size: params.size });
      } else if (filterComplexity !== 'all') {
        return hackApi.listByComplexity(filterComplexity, { page: params.page, size: params.size });
      } else {
        return hackApi.list({ page: params.page, size: params.size, sortBy: 'createdAt', sortDirection: 'desc' });
      }
    };
  }, [searchQuery, filterCategory, filterComplexity]);

  // Use paginated list hook
  const {
    data: hacks,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Hack>({
    fetchFn: fetchHacksFn,
    entityName: 'hacks',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (hackId: string) => {
      await hackApi.delete(hackId);
    },
    entityName: 'hack',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (hackId: string) => {
    if (!window.confirm('Are you sure you want to delete this hack?')) return;
    await deleteMutation.mutate(hackId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: HackStatus) => {
    switch (status) {
      case 'ACTIVE':
      case 'FEATURED':
      case 'TRENDING':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'UNDER_REVIEW':
      case 'IN_PROGRESS':
      case 'NOT_STARTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED':
      case 'DEPRECATED':
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const hackId = (hack: Hack) => hack.hackId || hack.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hack Management</h1>
          <p className="text-muted-foreground mt-2">Manage all hacks</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/hacks/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Hack
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search hacks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="PRODUCTIVITY">Productivity</SelectItem>
                <SelectItem value="EFFICIENCY">Efficiency</SelectItem>
                <SelectItem value="AUTOMATION">Automation</SelectItem>
                <SelectItem value="SHORTCUT">Shortcut</SelectItem>
                <SelectItem value="OPTIMIZATION">Optimization</SelectItem>
                <SelectItem value="WORKFLOW">Workflow</SelectItem>
                <SelectItem value="TIME_SAVING">Time Saving</SelectItem>
                <SelectItem value="LEARNING_ACCELERATOR">Learning Accelerator</SelectItem>
                <SelectItem value="PROBLEM_SOLVING">Problem Solving</SelectItem>
                <SelectItem value="CREATIVE">Creative</SelectItem>
                <SelectItem value="TECHNICAL">Technical</SelectItem>
                <SelectItem value="BEHAVIORAL">Behavioral</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterComplexity} onValueChange={setFilterComplexity}>
              <SelectTrigger>
                <SelectValue placeholder="All Complexities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Complexities</SelectItem>
                <SelectItem value="SIMPLE">Simple</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="COMPLEX">Complex</SelectItem>
                <SelectItem value="ADVANCED">Advanced</SelectItem>
                <SelectItem value="EXPERT_LEVEL">Expert Level</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Hacks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Hacks ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all hacks</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading hacks..." />
          ) : (error || hacks.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load hacks. Please try again or create a new one." : "Get started by creating your first hack."}
              icon={Zap}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/hacks/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hacks.map((hack) => (
                    <TableRow key={hackId(hack)}>
                      <TableCell className="font-medium">{hack.title}</TableCell>
                      <TableCell>{hack.category}</TableCell>
                      <TableCell>{hack.hackType}</TableCell>
                      <TableCell>{hack.complexity}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(hack.status)}`}>
                          {hack.status}
                        </span>
                      </TableCell>
                      <TableCell>{hack.hackGenerated ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {formatDate(hack.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/hacks/${hackId(hack)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/hacks/${hackId(hack)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(hackId(hack))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HackListPage;
